import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { useActiveDisplay } from "@/desktop/use-active-display";
import { isTauri } from "@/lib/is-tauri";

interface HoverBounds {
  left: number;
  top: number;
  right: number;
  bottom: number;
}

/** Hover for a rect that may move every frame. The rect lives in a ref, so
 * moving it never re-renders; only a flip of the hovered boolean does. */
export function useRectHover(
  origin: { x: number; y: number },
  displayId?: number,
) {
  const rectRef = useRef<HoverBounds | null>(null);
  const pointerRef = useRef<{ x: number; y: number } | null>(null);
  const [hovered, setHovered] = useState(false);
  const originX = origin.x;
  const originY = origin.y;
  const evaluate = useCallback(() => {
    const rect = rectRef.current;
    let point = pointerRef.current;
    if (isTauri()) {
      // Native cursor positions still arrive while the overlay passes
      // clicks through.
      const state = useActiveDisplay.getState();
      point =
        state.activeDisplayId === displayId
          ? { x: state.cursorX - originX, y: state.cursorY - originY }
          : null;
    }
    setHovered(
      !!rect &&
        !!point &&
        point.x >= rect.left &&
        point.x <= rect.right &&
        point.y >= rect.top &&
        point.y <= rect.bottom,
    );
  }, [originX, originY, displayId]);
  useEffect(() => {
    evaluate();
    if (isTauri()) return useActiveDisplay.subscribe(evaluate);
    const move = (event: PointerEvent) => {
      pointerRef.current = { x: event.clientX, y: event.clientY };
      evaluate();
    };
    const leave = () => {
      pointerRef.current = null;
      evaluate();
    };
    document.addEventListener("pointermove", move, { passive: true });
    document.addEventListener("pointerleave", leave);
    return () => {
      document.removeEventListener("pointermove", move);
      document.removeEventListener("pointerleave", leave);
    };
  }, [evaluate]);
  const setRect = useCallback(
    (rect: HoverBounds | null) => {
      rectRef.current = rect;
      evaluate();
    },
    [evaluate],
  );
  return { hovered, setRect };
}

export function useAnnotationHover(
  bounds: HoverBounds | null,
  origin: { x: number; y: number },
  displayId?: number,
) {
  const { hovered, setRect } = useRectHover(origin, displayId);
  const left = bounds?.left;
  const top = bounds?.top;
  const right = bounds?.right;
  const bottom = bounds?.bottom;
  useLayoutEffect(() => {
    setRect(
      left === undefined ||
        top === undefined ||
        right === undefined ||
        bottom === undefined
        ? null
        : { left, top, right, bottom },
    );
  }, [left, top, right, bottom, setRect]);
  return hovered;
}
